<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Copy, Layers, Pencil, X } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import {
    formatMoney,
    minorToInputValue,
    parseMoneyToMinor,
    bpsToInputValue,
    parsePercentToBps,
    formatPercent,
    clamp
  } from '$lib/utils/money';
  import AddFromCatalog from './AddFromCatalog.svelte';
  import type { QuotationSection, LineItem } from '$lib/types';

  const {
    quotationId,
    section,
    currency,
    canEditBOQ,
    pricingEditable
  }: {
    quotationId: number;
    section: QuotationSection;
    currency: string;
    canEditBOQ: boolean;
    pricingEditable: boolean;
  } = $props();

  const queryClient = useQueryClient();
  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] });
  }

  const lineItemMut = createMutation(() => ({
    mutationFn: ({ lineItemId, body }: { lineItemId: number; body: Record<string, unknown> }) =>
      quotationsApi.updateLineItem(quotationId, section.id, lineItemId, body),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const removeItemMut = createMutation(() => ({
    mutationFn: (lineItemId: number) => quotationsApi.deleteLineItem(quotationId, section.id, lineItemId),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const addItemMut = createMutation(() => ({
    mutationFn: (productId: number) =>
      quotationsApi.createLineItem(quotationId, section.id, { product_id: productId }),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const deleteSectionMut = createMutation(() => ({
    mutationFn: () => quotationsApi.deleteSection(quotationId, section.id),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const duplicateSectionMut = createMutation(() => ({
    mutationFn: () => quotationsApi.duplicateSection(quotationId, section.id),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const renameSectionMut = createMutation(() => ({
    mutationFn: (name: string) => quotationsApi.updateSection(quotationId, section.id, { name }),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function renameSection(value: string) {
    if (value.trim() && value !== section.name) renameSectionMut.mutate(value);
  }

  const sectionQtyMut = createMutation(() => ({
    mutationFn: (quantity: number) => quotationsApi.updateSection(quotationId, section.id, { quantity }),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function updateSectionQty(el: HTMLInputElement, value: string) {
    const parsed = parseInt(value, 10);
    const qty = Math.max(1, Number.isFinite(parsed) ? parsed : 1);
    el.value = String(qty);
    if (qty !== section.quantity) sectionQtyMut.mutate(qty);
  }

  // Each handler clamps to the valid range before saving, then writes the
  // clamped/persisted value back into the input — so a field never keeps
  // showing an unsaved or out-of-range value (e.g. a negative qty/price/%).
  function updateQty(el: HTMLInputElement, lineItemId: number, value: string) {
    const parsed = parseFloat(value);
    const qty = Math.max(1, Number.isFinite(parsed) ? parsed : 1);
    el.value = String(qty);
    lineItemMut.mutate({ lineItemId, body: { quantity: qty } });
  }
  function updateUnitPrice(el: HTMLInputElement, lineItemId: number, value: string) {
    const minor = Math.max(0, parseMoneyToMinor(value));
    el.value = minorToInputValue(minor);
    lineItemMut.mutate({ lineItemId, body: { unit_price_minor: minor } });
  }
  function updateDiscount(el: HTMLInputElement, lineItemId: number, value: string) {
    const bps = clamp(parsePercentToBps(value), 0, 10000);
    el.value = bpsToInputValue(bps);
    lineItemMut.mutate({ lineItemId, body: { discount_percent_bps: bps } });
  }
  function updateDescription(it: LineItem, value: string) {
    const trimmed = value.trim();
    if (!trimmed || trimmed === it.description) return;
    lineItemMut.mutate({ lineItemId: it.id, body: { description: trimmed } });
  }

  function removeSection() {
    if (confirm(`Remove section "${section.name}"? This deletes its line items too.`)) {
      deleteSectionMut.mutate();
    }
  }
</script>

<div class="tym-card">
  <div class="group/header flex items-center justify-between px-5 py-4 border-b border-toyama-cloudy-gray bg-toyama-light-carbon rounded-t-[inherit]">
    <div class="flex items-center gap-2.5">
      <Layers class="h-[17px] w-[17px] text-toyama-teal" />
      {#if canEditBOQ}
        <div class="relative flex items-center">
          <input
            class="font-heading font-bold text-[15.5px] text-navy bg-transparent border-0 border-b border-transparent outline-none px-0 py-0.5 min-w-0 rounded-sm transition-colors hover:bg-white/60 hover:border-toyama-cloudy-gray focus:bg-white/80 focus:border-toyama-teal"
            value={section.name}
            onchange={(e) => renameSection((e.currentTarget as HTMLInputElement).value)}
          />
          <Pencil class="h-3 w-3 text-dark-gray ml-1 shrink-0 opacity-0 group-hover/header:opacity-60" />
        </div>
      {:else}
        <span class="font-heading font-bold text-[15.5px] text-navy">{section.name}</span>
      {/if}
      <span class="text-[11.5px] text-dark-gray">{section.line_items.length} {section.line_items.length === 1 ? 'item' : 'items'}</span>
      {#if canEditBOQ}
        <label class="flex items-center gap-1 text-[11.5px] text-dark-gray">
          Qty ×
          <input
            type="number"
            class="pq-num-input !w-12 shrink-0"
            min="1"
            step="1"
            value={section.quantity}
            onchange={(e) => updateSectionQty(e.currentTarget as HTMLInputElement, (e.currentTarget as HTMLInputElement).value)}
          />
        </label>
      {:else if section.quantity > 1}
        <span class="text-[11.5px] text-dark-gray">× {section.quantity}</span>
      {/if}
    </div>
    <div class="flex items-center gap-3">
      <span class="font-heading font-bold text-[15px] text-navy">{formatMoney(section.section_total_minor, currency)}</span>
      {#if canEditBOQ}
        <button
          type="button"
          class="text-dark-gray hover:text-toyama-teal"
          disabled={duplicateSectionMut.isPending}
          onclick={() => duplicateSectionMut.mutate()}
          aria-label="Duplicate section"
        >
          <Copy class="h-4 w-4" />
        </button>
        <button type="button" class="text-dark-gray hover:text-red-600" onclick={removeSection} aria-label="Remove section">
          <X class="h-4 w-4" />
        </button>
      {/if}
    </div>
  </div>

  <div class="overflow-x-auto pq-scroll">
    <table class="w-full border-collapse min-w-[640px]">
      <thead>
        <tr>
          <th class="text-left px-5 py-2.5 text-[10px] font-bold tracking-[.05em] uppercase text-navy border-b border-toyama-cloudy-gray">
            Description
          </th>
          <th class="text-right px-2.5 py-2.5 text-[10px] font-bold tracking-[.05em] uppercase text-navy border-b border-toyama-cloudy-gray w-[70px]">
            Qty
          </th>
          <th class="text-right px-2.5 py-2.5 text-[10px] font-bold tracking-[.05em] uppercase text-navy border-b border-toyama-cloudy-gray w-[120px]">
            Unit price
          </th>
          <th class="text-right px-2.5 py-2.5 text-[10px] font-bold tracking-[.05em] uppercase text-navy border-b border-toyama-cloudy-gray w-[78px]">
            Disc %
          </th>
          <th class="text-right px-5 py-2.5 text-[10px] font-bold tracking-[.05em] uppercase text-navy border-b border-toyama-cloudy-gray w-[118px]">
            Total
          </th>
          <th class="border-b border-toyama-cloudy-gray w-[34px]"></th>
        </tr>
      </thead>
      <tbody>
        {#each section.line_items as it (it.id)}
          <tr class="border-b border-toyama-light-carbon">
            <td class="px-5 py-2.5">
              {#if pricingEditable}
                <input
                  class="text-[13.5px] font-medium text-navy leading-tight bg-transparent border-0 border-b border-transparent outline-none px-0 py-0.5 w-full min-w-0 rounded-sm transition-colors hover:bg-white/60 hover:border-toyama-cloudy-gray focus:bg-white/80 focus:border-toyama-teal"
                  value={it.description}
                  onchange={(e) => updateDescription(it, (e.currentTarget as HTMLInputElement).value)}
                />
              {:else}
                <div class="text-[13.5px] font-medium text-navy leading-tight">{it.description}</div>
              {/if}
              {#if it.part_number}
                <div class="text-[11px] text-dark-gray font-mono mt-0.5">{it.part_number}</div>
              {/if}
            </td>
            <td class="px-2.5 py-2 text-right">
              {#if canEditBOQ}
                <input
                  type="number"
                  class="pq-num-input w-[54px]"
                  min="1"
                  step="any"
                  value={it.quantity}
                  onchange={(e) => updateQty(e.currentTarget as HTMLInputElement, it.id, (e.currentTarget as HTMLInputElement).value)}
                />
              {:else}
                <!-- Plain text once qty can no longer be edited (e.g. an
                     approved quote) — a disabled input's value doesn't show
                     up in text search/copy/print/accessibility tooling,
                     which made a correctly-stored quantity look blank. -->
                <span class="text-[13.5px] text-navy">{it.quantity}</span>
              {/if}
            </td>
            <td class="px-2.5 py-2 text-right">
              {#if pricingEditable}
                <input
                  type="number"
                  class="pq-num-input w-[104px]"
                  step="0.01"
                  min="0"
                  value={minorToInputValue(it.unit_price_minor)}
                  onchange={(e) => updateUnitPrice(e.currentTarget as HTMLInputElement, it.id, (e.currentTarget as HTMLInputElement).value)}
                />
              {:else}
                <span class="text-[13.5px] text-navy">{formatMoney(it.unit_price_minor, currency)}</span>
              {/if}
            </td>
            <td class="px-2.5 py-2 text-right">
              {#if pricingEditable}
                <input
                  type="number"
                  class="pq-num-input w-[60px]"
                  step="0.01"
                  min="0"
                  max="100"
                  value={bpsToInputValue(it.discount_percent_bps)}
                  onchange={(e) => updateDiscount(e.currentTarget as HTMLInputElement, it.id, (e.currentTarget as HTMLInputElement).value)}
                />
              {:else}
                <span class="text-[13.5px] text-navy">{formatPercent(it.discount_percent_bps)}</span>
              {/if}
            </td>
            <td class="px-5 py-2.5 text-right text-[13.5px] font-semibold text-navy whitespace-nowrap">
              {formatMoney(it.line_total_minor, currency)}
            </td>
            <td class="px-1.5 py-2 text-center">
              {#if canEditBOQ}
                <button
                  type="button"
                  title="Remove"
                  class="text-dark-gray hover:text-red-600 p-1"
                  onclick={() => removeItemMut.mutate(it.id)}
                >
                  <X class="h-[15px] w-[15px]" />
                </button>
              {/if}
            </td>
          </tr>
        {:else}
          <tr><td colspan="6" class="text-center text-dark-gray text-[12.5px] py-5">No line items yet.</td></tr>
        {/each}
      </tbody>
    </table>
  </div>

  {#if canEditBOQ}
    <AddFromCatalog disabled={addItemMut.isPending} onAdd={(id) => addItemMut.mutate(id)} />
  {/if}
</div>
